import React, { useEffect } from 'react';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@rocco/components/popover';
import { RadioGroup, RadioGroupItem } from '@rocco/components/radio-group';
import { Button } from '@rocco/components/button';
import { ReactComponent as ArrowChevronDownIcon } from '@rocco/icons/svg/arrow/arrow-chevron-down.svg';
import {
    type FilterSortOption,
    useFiltersContext,
} from '@rocco/ui/filter-sort/components/Filters';
import { validateSortOption } from '@rocco/types/enum/sort-option';
import { useBoolean } from 'ahooks';

interface SortDropdownProps {
    sortOptions: FilterSortOption[];
}

export const SortDropdown = (props: SortDropdownProps) => {
    const { sortOptions } = props;
    const { searchFilters } = useFiltersContext();
    const { updateSort } = searchFilters;

    const activeOption = sortOptions.find(option => option.selected);
    const defaultOption = activeOption?.value ?? sortOptions?.[0]?.value ?? '';
    const activatedIndex = sortOptions.findIndex(option => option.selected);

    const handleSortChange = (value: string) => {
        const sortOption = validateSortOption(value);

        if (sortOption) {
            updateSort(sortOption);
            setIsOpen(false);
        }
    };

    const [isOpen, { set: setIsOpen, setFalse: closeSortDropdown }] =
        useBoolean(false);

    useEffect(() => {
        let lastScrollY = window.scrollY;

        const handleScroll = () => {
            const scrollDiff = Math.abs(window.scrollY - lastScrollY);
            if (scrollDiff > 300) {
                closeSortDropdown();
                lastScrollY = window.scrollY;
            }
        };

        window.addEventListener('scroll', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [closeSortDropdown]);

    return sortOptions.length && sortOptions[0] ? (
        <Popover open={isOpen} onOpenChange={setIsOpen}>
            <PopoverTrigger asChild>
                <Button color="transparent" size="compact">
                    <span>
                        {activatedIndex > 0 ? activeOption?.title : 'Sort By'}
                    </span>
                    <ArrowChevronDownIcon className="ml-2 size-4.5 transition-transform group-data-[state=open]:[transform:rotateX(180deg)]" />
                </Button>
            </PopoverTrigger>
            <PopoverContent align="end" paddings="small">
                <RadioGroup
                    defaultValue={defaultOption}
                    onValueChange={handleSortChange}
                >
                    {sortOptions.map((option, index) => (
                        <label
                            key={index}
                            className="flex items-center space-x-3 rounded-2 px-2 py-2.5 hover:bg-gray-50 cursor-pointer"
                        >
                            <RadioGroupItem value={option.value} />
                            <span className="text-base">{option.title}</span>
                        </label>
                    ))}
                </RadioGroup>
            </PopoverContent>
        </Popover>
    ) : null;
};
